import { useState, useCallback } from "react"

import { useNavigate } from "react-router-dom"
import { Box, Container, Grid2 as Grid, useMediaQuery, useTheme } from "@mui/material"
import {
    MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { DateTime } from "luxon"

import {
    AuthLibMSALTokenAcquisitionError,
    useMSALPayload
} from "@initia-digital/auth-client-utils"
import {
    BasicLoader, CriticalOrSiteSpecificHazardListItemReadOnly, GenericAutosuggest,
    IconButton, PlanAndPrestartMultiLine,
    PlanListItem, PrestartListItem, Typography, useSnackbar,
    WorkAndSafetyJobActionToggleButtonGroup
} from "@initia-digital/component-library"
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils"

import { useIsAdmin, useJobSearch, useFullTeamList } from "../hooks"
import { fetchOlapQuery, fetchPlanList, fetchPrestartList } from "../api/query"
import { BasicAppBarPage } from "../layouts/BasicAppBarPage"
import { patchPlan, patchPrestart } from "../api/mutate"
import { comparePlanDates, comparePrestartDates, filterPlanByDate } from "@initia-digital/data-service-mapping"
import { EmergencyDirectiveBanner } from "../legacy/EmergencyDirectiveBanner"


const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const Index = () => {
    // --------------------------------------------
    const [actionState, setActionState] = useState<'View Job' | 'New Plan'>("View Job");
    const actionStateHelperText = {
        "View Job": "Search to view plans and prestarts",
        "New Plan": "Search to create a plan"
    }

    const navigate = useNavigate();
    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();
    const isAdmin = useIsAdmin(authDetail, true);

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    // DATA INTEGRATION
    const queryClient = useQueryClient();
    const searchJob = useJobSearch(authDetail);

    // PLAN LISTS
    const { isLoading: isPrevPlanListLoading, data: prevPlanList } = useQuery({
        queryKey: ['previousMonthPlanList'],
        queryFn: () => fetchPlanList(authDetail, {
            planMonth: DateTime.local().minus({ month: 1 }).toFormat('yyyy-MM')
        })
    });
    const { isLoading: isPlanListLoading, data: planList } = useQuery({
        queryKey: ['currentMonthPlanList'],
        queryFn: () => fetchPlanList(authDetail, {
            planMonth: DateTime.local().toFormat('yyyy-MM')
        })
    });
    const { isLoading: isNextPlanListLoading, data: nextPlanList } = useQuery({
        queryKey: ['nextMonthPlanList'],
        queryFn: () => fetchPlanList(authDetail, {
            planMonth: DateTime.local().plus({ month: 1 }).toFormat('yyyy-MM')
        })
    });

    // PRESTART LISTS
    const { isLoading: isPrevPrestartListLoading, data: prevPrestartList } = useQuery({
        queryKey: ['previousMonthPrestartList'],
        queryFn: () => fetchPrestartList(authDetail, {
            prestartDate: DateTime.local().minus({ month: 1 }).toFormat('yyyy-MM')
        })
    });
    const { isLoading: isPrestartListLoading, data: prestartList } = useQuery({
        queryKey: ['currentMonthPrestartList'],
        queryFn: () => fetchPrestartList(authDetail, {
            prestartDate: DateTime.local().toFormat('yyyy-MM')
        })
    });
    const { isLoading: isNextPrestartListLoading, data: nextPrestartList } = useQuery({
        queryKey: ['nextMonthPrestartList'],
        queryFn: () => fetchPrestartList(authDetail, {
            prestartDate: DateTime.local().plus({ month: 1 }).toFormat('yyyy-MM')
        })
    });

    // TEAM LIST
    const { fullTeamListLoading, fullTeamListData } = useFullTeamList(authDetail, true);

    // OLAP MONTH SUMMARY
    const { isLoading: isOlapMonthSummaryLoading, data: olapMonthSummary } = useQuery({
        queryKey: ['olapMonthSummary'],
        queryFn: () => fetchOlapQuery<{ monthSummary: 'true' }>(authDetail, {
            monthSummary: 'true'
        })
    });

    // MUTATIONS
    const patchPlanMutation = useMutation({
        mutationFn: patchPlan,
        onSuccess: (data) => {
            // ---------------------
            queueSnackbar(`The plan has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['plan', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating plan', { error });
            }
            logger.error('Error updating plan');
            queueSnackbar('Plan could not be updated', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    const patchPrestartMutation = useMutation({
        mutationFn: patchPrestart,
        onSuccess: (data) => {
            queueSnackbar(`The prestart has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['prestart', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'prestart'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPrestartList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating prestart', { error });
            }
            logger.error('Error updating prestart');
            queueSnackbar('Prestart could not be updated', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });

    // EVENT HANDLERS
    const handleJobSelect = useCallback((job: Record<string, string | number | boolean | null | undefined>) => {
        if ('jobId' in job) {
            const { jobId } = job;
            navigate(actionState === 'New Plan' ? `/job/${jobId}/plan` : `/job/${jobId}`);
        }
    }, [actionState, navigate]);


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: "25px" }}>
                <EmergencyDirectiveBanner />
                <Grid
                    container
                    columnSpacing={1.5} rowSpacing={2} alignItems="stretch"
                    mb="32px"
                >
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column" width="100%"
                            px="10px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                color={theme.palette.secondary.main} fontWeight={700}
                            >
                                Get started
                            </Typography>
                            <Box display="flex" flexDirection="column" rowGap="5px">
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Choose an action and search for your job
                                </Typography>
                                <WorkAndSafetyJobActionToggleButtonGroup
                                    size="small"
                                    defaultValue={'View Job'}
                                    value={actionState}
                                    onChange={(value) => {
                                        // ---------------------
                                        if (!baseDefinedAndNotNullNarrowing(value)) {
                                            return;
                                        }
                                        setActionState(value as 'View Job' | 'New Plan')
                                    }}
                                />
                            </Box>
                            <Box
                                pt="15px"
                            >
                                <GenericAutosuggest
                                    label={actionStateHelperText[actionState]}
                                    placeholder={`Enter your job number`}
                                    labelKey="jobId"
                                    secondaryKeysForDisplay={["jobId"]}
                                    uniqueOptionKeyProps={["jobId"]}
                                    suggestionKeysForOptionDisplay={["jobId", "jobName"]}
                                    suggestionKeysToSearchOption={["jobId", "jobName"]}
                                    handleChange={handleJobSelect}
                                    searchSuggestion={searchJob}
                                />
                            </Box>

                            <Box
                                pt="15px"
                                pb="5px"
                            >
                                <Typography
                                    fontSize={"0.9rem"}
                                    fontWeight={700}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Latest critical and site-specific hazards
                                </Typography>
                                {
                                    isOlapMonthSummaryLoading || !baseDefinedAndNotNullNarrowing(olapMonthSummary) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        baseDefinedAndNotNullNarrowing(olapMonthSummary.currentHighlightedHazards) &&
                                            olapMonthSummary.currentHighlightedHazards.length === 0 ?
                                            <Box display="flex" flexDirection="row" mt="25px" width="100%" justifyContent="center">
                                                <Typography
                                                    fontSize={"0.8rem"}
                                                    fontWeight={300}
                                                    color={theme.palette.primary.contrastText}
                                                >
                                                    No critical or site-specific hazards
                                                </Typography>
                                            </Box>
                                            :
                                            <Box
                                                display="flex" flexDirection="column" rowGap="8px"
                                                mt="5px" py="5px"
                                            >
                                                {
                                                    olapMonthSummary.currentHighlightedHazards.map((hazard) => {
                                                        return (
                                                            <CriticalOrSiteSpecificHazardListItemReadOnly
                                                                key={hazard.id}
                                                                hazard={hazard}
                                                                actionButtons={[
                                                                    <IconButton
                                                                        key={`view-hazards-${hazard.id}`}
                                                                        size="small"
                                                                        tooltipTitle="More detail"
                                                                        onClick={() => {
                                                                            // ---------------------
                                                                            navigate(`/job/${hazard.jobId}/edit-plan/${hazard.planId}?stage=hazardAssessment`);
                                                                        }}
                                                                    >
                                                                        <MoreHorizIcon />
                                                                    </IconButton>
                                                                ]}
                                                            />
                                                        )
                                                    })
                                                }
                                            </Box>
                                }
                            </Box>
                            <Box
                                pt="5px"
                                pb="25px"
                            >
                                <Typography
                                    fontSize={"0.9rem"}
                                    fontWeight={700}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Month Summary
                                </Typography>
                                {
                                    isOlapMonthSummaryLoading || !baseDefinedAndNotNullNarrowing(olapMonthSummary) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            mt="5px"
                                        >
                                            <PlanAndPrestartMultiLine
                                                planData={olapMonthSummary.planMonthCounts}
                                                prestartData={olapMonthSummary.prestartMonthCounts}
                                            />
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box display="flex" flexDirection="column" width="100%">
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                            >
                                <Typography
                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                    color={theme.palette.secondary.main} fontWeight={700}
                                >
                                    Plans
                                </Typography>
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Choose an upcoming plan below or go to your job using the search
                                </Typography>
                                {
                                    fullTeamListLoading || isPrevPlanListLoading || isPlanListLoading || isNextPlanListLoading
                                        || !baseDefinedAndNotNullNarrowing(fullTeamListData)
                                        || !baseDefinedAndNotNullNarrowing(prevPlanList)
                                        || !baseDefinedAndNotNullNarrowing(planList)
                                        || !baseDefinedAndNotNullNarrowing(nextPlanList) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            display="flex" flexDirection="column" justifyContent="center"
                                            mt="10px"
                                        >
                                            {
                                                (prevPlanList.length === 0 && planList.length === 0 && nextPlanList.length === 0) ?
                                                    <Box
                                                        sx={{
                                                            py: "25px"
                                                        }}
                                                    >
                                                        <Typography
                                                            fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                                            fontWeight={700}
                                                            color={theme.palette.primary.contrastText}
                                                        >
                                                            No upcoming plans
                                                        </Typography>
                                                    </Box>
                                                    :
                                                    [...prevPlanList, ...planList, ...nextPlanList]
                                                        .filter(plan => filterPlanByDate(plan, 'in_progress_or_upcoming_plus_recently_finished'))
                                                        .sort((a, b) => comparePlanDates(a, b, 'in_progress_upcoming_other_with_priority_subsort'))
                                                        .map((plan) => {
                                                            return (
                                                                <PlanListItem
                                                                    key={plan.id}
                                                                    plan={plan}
                                                                    isAdmin={isAdmin}
                                                                    teamMemberList={fullTeamListData}
                                                                    handleStatusChange={(planId, status, notify = false) => {
                                                                        // ----------------------------------------
                                                                        patchPlanMutation.mutateAsync({
                                                                            authDetail,
                                                                            planId,
                                                                            patches: [
                                                                                {
                                                                                    "op": "replace",
                                                                                    "path": "/status",
                                                                                    "value": status
                                                                                }
                                                                            ],
                                                                            notify
                                                                        });
                                                                    }}
                                                                    handleEdit={() => navigate(`/job/${plan.jobId}/edit-plan/${plan.id}`)}
                                                                    handleView={() => navigate(`/job/${plan.jobId}/view-plan/${plan.id}`)}
                                                                    handleNewPrestart={() => navigate(`/job/${plan.jobId}/plan/${plan.id}/prestart`)}
                                                                />
                                                            )
                                                        })
                                            }
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box display="flex" flexDirection="column" width="100%">
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                            >
                                <Typography
                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                    color={theme.palette.secondary.main} fontWeight={700}
                                >
                                    Prestarts/Take-5's
                                </Typography>
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Check out the latest prestarts and Take-5's being completed on site
                                </Typography>
                                {
                                    fullTeamListLoading || isPrevPrestartListLoading || isPrestartListLoading || isNextPrestartListLoading
                                        || !baseDefinedAndNotNullNarrowing(fullTeamListData)
                                        || !baseDefinedAndNotNullNarrowing(prevPrestartList)
                                        || !baseDefinedAndNotNullNarrowing(prestartList)
                                        || !baseDefinedAndNotNullNarrowing(nextPrestartList) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            display="flex" flexDirection="column" justifyContent="center"
                                            mt="10px"
                                        >
                                            {
                                                (prevPrestartList.length === 0 && prestartList.length === 0 && nextPrestartList.length === 0) ?
                                                    <Box
                                                        sx={{
                                                            py: "25px"
                                                        }}
                                                    >
                                                        <Typography
                                                            fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                                            fontWeight={700}
                                                            color={theme.palette.primary.contrastText}
                                                        >
                                                            No recent prestarts or Take-5's
                                                        </Typography>
                                                    </Box>
                                                    :
                                                    [...prevPrestartList, ...prestartList, ...nextPrestartList]
                                                        .sort(comparePrestartDates)
                                                        .map((prestart) => {
                                                            return (
                                                                <PrestartListItem
                                                                    key={`prestartlistitem-${prestart.id}`}
                                                                    prestart={prestart}
                                                                    isAdmin={isAdmin}
                                                                    teamMemberList={fullTeamListData}
                                                                    handleStatusChange={(prestartId, status, notify = false) => {
                                                                        // ----------------------------------------
                                                                        patchPrestartMutation.mutateAsync({
                                                                            authDetail,
                                                                            prestartId,
                                                                            patches: [
                                                                                {
                                                                                    "op": "replace",
                                                                                    "path": "/status",
                                                                                    "value": status
                                                                                }
                                                                            ],
                                                                            notify
                                                                        });
                                                                    }}
                                                                    handleEdit={() => navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/edit-prestart/${prestart.id}`)}
                                                                    handleView={() => navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/view-prestart/${prestart.id}`)}
                                                                    handleViewPlan={() => navigate(`/job/${prestart.jobId}/view-plan/${prestart.planId}`)}
                                                                />
                                                            )
                                                        })
                                            }
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </BasicAppBarPage>
    )
}

export default Index;